import { InputCalendar, SelectStatusInput } from '@spo/storybook';
import { VpDivider, VpIcon } from '@vtmn-play/react';
import { addDays, differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { todayDateAdd20years, tomorrowDate } from '../../utils/date';
import { StatusState } from '../navigation';
import './massEditInputs.scss';

type MassEditInputsProps = {
  startDateSelected: Date[];
  endDateSelected: Date[];
  setStartDateSelected: (value: Date[]) => void;
  setEndDateSelected: (value: Date[]) => void;
  disabledStartDateInput: boolean;
  disabledDates?: boolean;
  setSelectedStatus?: (status: string) => void;
  selectedStatus?: string;
  statusSelection?: StatusState[];
  massEditTitleKey?: string;
};

export const MassEditInputs = ({
  startDateSelected,
  endDateSelected,
  selectedStatus = '',
  setStartDateSelected,
  setEndDateSelected,
  setSelectedStatus,
  disabledStartDateInput,
  disabledDates = false,
  statusSelection,
  massEditTitleKey = 'header.mass-edit_sms_title',
}: MassEditInputsProps) => {
  const { t } = useTranslation();
  const [displayMessage, setDisplayMessage] = useState(false);

  const minSelectableDateEndDateInput = () => {
    return startDateSelected.length > 0
      ? [addDays(startDateSelected[0], 9)] //add 9 instead of 8 to match the behavior of component Calendar.tsx l.59
      : [tomorrowDate];
  };

  const isStartAndEndDateHave8DaysDiff = () => {
    if (startDateSelected[0] && endDateSelected[0]) {
      return (
        Math.abs(differenceInDays(startDateSelected[0], endDateSelected[0])) > 7
      );
    }
    return true;
  };

  useEffect(() => {
    if (
      endDateSelected[0] < startDateSelected[0] ||
      !isStartAndEndDateHave8DaysDiff()
    ) {
      setEndDateSelected([]);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayMessage(true);
    }
    if (
      endDateSelected[0] &&
      displayMessage &&
      isStartAndEndDateHave8DaysDiff()
    ) {
      setDisplayMessage(false);
    }
  }, [startDateSelected, endDateSelected]);

  return (
    <div data-testid="mass-edit_inputs" className="mass-edit-inputs-container">
      <div className="mass-edit-inputs">
        <div
          data-testid="mass-edit_inputs-title"
          className="mass-edit-inputs-title"
        >
          <p>{t(massEditTitleKey)}</p>
          <span>{t('mass-edit-inputs.subtitle')}</span>
        </div>
        <div
          data-testid="mass-edit_inputs-select_container"
          className="mass-edit-inputs-select-container"
        >
          <InputCalendar
            minSelectableDate={[tomorrowDate]}
            maxSelectableDate={[todayDateAdd20years]}
            selectedDate={startDateSelected}
            onChange={setStartDateSelected}
            labelInput={t('mass-edit.start-date')}
            index={1}
            disabled={disabledStartDateInput || disabledDates}
          />
          <InputCalendar
            minSelectableDate={minSelectableDateEndDateInput()}
            maxSelectableDate={[todayDateAdd20years]}
            selectedDate={endDateSelected}
            onChange={setEndDateSelected}
            labelInput={t('mass-edit.end-date')}
            align="right"
            index={2}
            disabled={disabledDates}
          />
          {statusSelection && setSelectedStatus && (
            <SelectStatusInput
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              availableStatus={statusSelection}
              label={t('mass-edit-inputs.status_label')}
            />
          )}
        </div>
      </div>
      {displayMessage && (
        <div
          className="mass-edit-inputs-error-message-container"
          data-testid="mass-edit-inputs-error-message-container"
        >
          <VpDivider />
          <div className="mass-edit-inputs-error-message">
            <VpIcon name="warning-error" size={24} />
            {t('mass-edit.end-date_error')}
          </div>
        </div>
      )}
    </div>
  );
};
